import React from "react";
import PropTypes from "prop-types";
import { Checkbox, DatePicker, Dropdown, Input } from "@boozallen-uip/uswds-react-core";
import {
  FormField,
  FormFieldBody,
  FormFieldContainer,
  FormSectionContainer,
  FormSectionHeader,
  FormSectionHeaderContainer,
  FormSectionSubHeader,
} from "../../shared.styled";

function normalizeAuthoredDateForState(value) {
  const raw = String(value ?? "").trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return raw;
  }
  const usMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    return `${year}-${month}-${day}`;
  }
  return raw;
}

export default function AcrInformationSection({
  hyperlink,
  acrTypes,
  clearFieldErrors,
  setHyperlinkName,
  setHyperlink,
  normalizeHyperlinkUrlForSave,
  getFieldError,
  renderInlineFieldError,
  FormFieldRequired,
  REQUEST_LINK_MESSAGE,
}) {
  React.useEffect(() => {
    const setDateInputAttributes = () => {
      const hiddenInput = document.getElementById("authoredDate");
      const datePickerContainer = hiddenInput?.closest(".usa-date-picker");
      const externalInput = datePickerContainer?.querySelector(".usa-date-picker__external-input");
      if (!externalInput) return false;

      externalInput.setAttribute("autocomplete", "off");
      externalInput.setAttribute("maxlength", "10");
      return true;
    };

    if (setDateInputAttributes()) {
      return undefined;
    }

    const root = document.getElementById("authoredDate")?.closest(".usa-date-picker");
    if (!root) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (setDateInputAttributes()) {
        observer.disconnect();
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <FormSectionContainer className="col-span-8">
      <FormSectionHeaderContainer>
        <FormSectionHeader>ACR Information</FormSectionHeader>
        <FormSectionSubHeader>
          <p className="mb-1">The information below should reflect the information in your ACR.</p>
        </FormSectionSubHeader>
      </FormSectionHeaderContainer>
      <FormFieldContainer>
        <FormFieldBody>
          <FormField className="order-1">
            <Input
              id="hyperlinkUrl"
              value={hyperlink.url ?? ""}
              placeholder="https://www.example.com/acr"
              disabled={hyperlink.requestRequired}
              onChange={(e) => {
                clearFieldErrors(["hyperlink.url"]);
                setHyperlinkName(e.target.value);
                setHyperlink({ ...hyperlink, url: e.target.value });
              }}
              onBlur={() => {
                const normalizedUrl = normalizeHyperlinkUrlForSave(hyperlink?.url);
                if (normalizedUrl && normalizedUrl !== hyperlink?.url) {
                  setHyperlink((prevHyperlink) => ({ ...prevHyperlink, url: normalizedUrl }));
                }
              }}
            >
              ACR link <FormFieldRequired />
            </Input>
            {renderInlineFieldError(getFieldError("hyperlink.url"))}
          </FormField>
          <FormField className="order-3 col-start-1 col-end-12">
            <Checkbox id="hyperlink_request"
              checked={Boolean(hyperlink.requestRequired)}
              value={hyperlink.requestRequired}
              onChange={e => {
                clearFieldErrors(["hyperlink.url"]);
                if (e.target.checked) {
                  setHyperlinkName(REQUEST_LINK_MESSAGE);
                } else {
                  setHyperlinkName(hyperlink.name);
                }
                setHyperlink({ ...hyperlink, requestRequired: e.target.checked });
              }}>Request link from product POC</Checkbox>
          </FormField>
          <FormField className="order-2">
            <Dropdown id="acrType"
              optionRenderer={(o) => o.label}
              options={acrTypes}
              comparator={(option, selected) => option?.value === selected || option?.label === selected}
              value={hyperlink.acrType || ""}
              onChange={e => {
                clearFieldErrors(["hyperlink.acrType"]);
                setHyperlink({ ...hyperlink, acrType: e.detail.value.value });
              }}
              required>
              ACR type <FormFieldRequired />
            </Dropdown>
            {renderInlineFieldError(getFieldError("hyperlink.acrType"))}
          </FormField>
          <FormField className="order-4">
            <DatePicker id="authoredDate"
              hint={null}
              value={hyperlink.authoredDate || ""}
              placeholder="MM/DD/YYYY"
              autoComplete="off"
              onChange={e => {
                clearFieldErrors(["hyperlink.authoredDate"]);
                setHyperlink(prevHyperlink => ({
                  ...prevHyperlink,
                  authoredDate: normalizeAuthoredDateForState(e.target.value),
                }));
              }}
            >
              Date the ACR was created <FormFieldRequired />
            </DatePicker>
            {renderInlineFieldError(getFieldError("hyperlink.authoredDate"))}
          </FormField>
          <FormField className="order-5">
            <Input id="productVersionInAcr" value={hyperlink.version ?? ""} onChange={(e) => setHyperlink({ ...hyperlink, version: e.target.value })}>
              Product version in ACR
            </Input>
          </FormField>
        </FormFieldBody>
      </FormFieldContainer>
    </FormSectionContainer>
  );
}

AcrInformationSection.propTypes = {
  hyperlink: PropTypes.object.isRequired,
  acrTypes: PropTypes.array.isRequired,
  clearFieldErrors: PropTypes.func.isRequired,
  setHyperlinkName: PropTypes.func.isRequired,
  setHyperlink: PropTypes.func.isRequired,
  normalizeHyperlinkUrlForSave: PropTypes.func.isRequired,
  getFieldError: PropTypes.func.isRequired,
  renderInlineFieldError: PropTypes.func.isRequired,
  FormFieldRequired: PropTypes.elementType.isRequired,
  REQUEST_LINK_MESSAGE: PropTypes.string.isRequired,
};
